import Input from "../../components/form/input/InputField";
import CustomMultiSelect from "../../components/common/CustomMultiSelect";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import axios from "axios";
import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import FileUpload from "../../components/form/FileUpload";
import { styles } from "../../styles/pages/User/UserAddStyles";
import ListboxComponent from "../../components/common/ListboxComponent";

interface Option {
  value: string;
  label: string;
}

interface JobTitle {
  id: number;
  job_title: string;
}

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  mobile_number: string;
  password: string;
  password_confirmation: string;
  selectedRoles: string[];
  profile_image: File | null;
  job_type?: string;
  job_title: string;
}

interface Errors {
  first_name?: string;
  last_name?: string;
  email?: string;
  mobile_number?: string;
  password?: string;
  password_confirmation?: string;
  roles?: string;
  job_type?: string;
  job_title?: string;
}

const UserAdd = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<UserFormData>({
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    password: "",
    password_confirmation: "",
    selectedRoles: [],
    profile_image: null,
    job_type: "",
    job_title: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);

  const roleOptions: Option[] = [
    { value: "marketing", label: "marketing" },
    { value: "sales", label: "sales" },
  ];

  useEffect(() => {
    const fetchJobTitles = async () => {
      const token = sessionStorage.getItem("access_token");
      if (!token) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/jobtitle/name/list`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.data.status === 200) {
          setJobTitles(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching job titles:", error);
      }
    };
    fetchJobTitles();
  }, []);

  const handleInputChange = (
    field: keyof UserFormData,
    value: string | string[] | File | null
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value as any }));
    if (typeof value === "string") {
      if (!value.trim()) {
        if (field === "first_name") {
          setErrors((prev) => ({
            ...prev,
            first_name: "The first name field is required",
          }));
        } else if (field === "last_name") {
          setErrors((prev) => ({
            ...prev,
            last_name: "The last name field is required",
          }));
        } else if (field === "email") {
          setErrors((prev) => ({
            ...prev,
            email: "The email field is required",
          }));
        } else if (field === "mobile_number") {
          setErrors((prev) => ({
            ...prev,
            mobile_number: "Mobile number field is required",
          }));
        } else if (field === "password") {
          setErrors((prev) => ({
            ...prev,
            password: "The password field is required",
          }));
        } else if (field === "password_confirmation") {
          setErrors((prev) => ({
            ...prev,
            password_confirmation: "The confirm password field is required",
          }));
        } else if (field === "job_type") {
          setErrors((prev) => ({
            ...prev,
            job_type: "The job type field is required",
          }));
        } else if (field === "job_title") {
          setErrors((prev) => ({
            ...prev,
            job_title: "The job title field is required",
          }));
        }
      } else {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    }
    if (field === "selectedRoles") {
      const roles = value as string[];
      setErrors((prev) => ({
        ...prev,
        roles: roles.length === 0 ? "At least one role must be selected" : undefined,
      }));
    }
  };

  const handleMobileNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, mobile_number: formattedValue }));
    if (!formattedValue) {
      setErrors((prev) => ({
        ...prev,
        mobile_number: "Mobile number field is required",
      }));
    } else {
      setErrors((prev) => ({ ...prev, mobile_number: undefined }));
    }
  };

  const validateForm = (): Errors => {
    const newErrors: Errors = {};
    if (!formData.first_name)
      newErrors.first_name = "The first name field is required";
    if (!formData.last_name)
      newErrors.last_name = "The last name field is required";
    if (!formData.email) newErrors.email = "The email field is required";
    if (!formData.mobile_number)
      newErrors.mobile_number = "Mobile number field is required";
    if (!formData.password)
      newErrors.password = "The password field is required";
    if (!formData.password_confirmation)
      newErrors.password_confirmation = "The confirm password field is required";
    if (formData.selectedRoles.length === 0)
      newErrors.roles = "At least one role must be selected";
    if (!formData.job_type)
      newErrors.job_type = "The job type field is required";
    if (!formData.job_title)
      newErrors.job_title = "The job title field is required";
    return newErrors;
  };

  const prepareFormData = (): FormData => {
    const data = new FormData();
    data.append("first_name", formData.first_name);
    data.append("last_name", formData.last_name);
    data.append("email", formData.email);
    data.append("mobile_number", formData.mobile_number);
    data.append("password", formData.password);
    data.append("password_confirmation", formData.password_confirmation);
    formData.selectedRoles.forEach((role) => data.append("role[]", role));
    if (formData.job_type) data.append("job_type", formData.job_type);
    if (formData.job_title) data.append("job_title", formData.job_title);
    if (formData.profile_image)
      data.append("profile_image", formData.profile_image);
    return data;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSaving(false);
      return;
    }

    const token = sessionStorage.getItem("access_token");
    if (!token) {
      setErrors({ email: "Authorization required. Please log in." });
      navigate("/signin");
      setSaving(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/add`,
        prepareFormData(),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
          setFormData({
            first_name: "",
            last_name: "",
            email: "",
            mobile_number: "",
            password: "",
            password_confirmation: "",
            selectedRoles: [],
            profile_image: null,
            job_type: "",
            job_title: "",
          });
          navigate("/user-list");
        }, 2000);
      } else if (response.data.status === 422) {
        const apiErrors = response.data.message;
        if (apiErrors && typeof apiErrors === "object") {
          setErrors(
            Object.fromEntries(
              Object.entries(apiErrors).map(([key, value]) => [
                key,
                Array.isArray(value) ? value[0] : value,
              ])
            ) as any
          );
        }
      } else if (response.data.status === 500) {
        setErrors({ email: response.data.message || "Internal Server Error" });
      } else {
        setErrors({ email: "Unexpected server response. Please try again." });
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      setErrors({ email: errorMessage });

      if (error.response?.status === 422) {
        const apiErrors = error.response?.data?.errors;
        if (apiErrors && typeof apiErrors === "object") {
          setErrors(
            Object.fromEntries(
              Object.entries(apiErrors).map(([key, value]) => [
                key,
                Array.isArray(value) ? value[0] : value,
              ])
            ) as any
          );
        }
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Add User</h2>
      </div>

      <div className={styles.formContainer}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.profileImageSection}>
            <label className={styles.label}>Profile Image</label>
            <div>
              <FileUpload
                onFileSelect={(file) => handleInputChange("profile_image", file)}
                showDelete={false}
              />
            </div>
          </div>

          <div className={styles.userDetails}>
            <div>
              <label className={styles.label}>
                First Name <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={formData.first_name}
                onChange={(e) => handleInputChange("first_name", e.target.value)}
                className={`${styles.inputField} ${errors.first_name ? styles.inputError : styles.inputDefault
                  }`}
              />
              {errors.first_name && (
                <span className={styles.errorText}>{errors.first_name}</span>
              )}
            </div>

            <div>
              <label className={styles.label}>
                Last Name <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={formData.last_name}
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                className={`${styles.inputField} ${errors.last_name ? styles.inputError : styles.inputDefault
                  }`}
              />
              {errors.last_name && (
                <span className={styles.errorText}>{errors.last_name}</span>
              )}
            </div>

            <div>
              <label className={styles.label}>
                Email <span className={styles.required}>*</span>
              </label>
              <Input
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`${styles.inputField} ${errors.email ? styles.inputError : styles.inputDefault
                  }`}
              />
              {errors.email && (
                <span className={styles.errorText}>{errors.email}</span>
              )}
            </div>

            <div>
              <label className={styles.label}>
                Mobile Number <span className={styles.required}>*</span>
              </label>
              <Input
                type="tel"
                value={formData.mobile_number}
                onChange={handleMobileNumber}
                className={`${styles.inputField} ${errors.mobile_number ? styles.inputError : styles.inputDefault
                  }`}
              />
              {errors.mobile_number && (
                <span className={styles.errorText}>{errors.mobile_number}</span>
              )}
            </div>

            <div>
              <label className={styles.label}>
                Password <span className={styles.required}>*</span>
              </label>
              <div className={styles.passwordContainer}>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  className={`${styles.inputField} pr-10 ${errors.password ? styles.inputError : styles.inputDefault
                    }`}
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.passwordToggle}
                >
                  {showPassword ? (
                    <EyeIcon className="fill-gray-500" />
                  ) : (
                    <EyeCloseIcon className="fill-gray-500" />
                  )}
                </span>
              </div>
              {errors.password && (
                <span className={styles.errorText}>{errors.password}</span>
              )}
            </div>

            <div>
              <label className={styles.label}>
                Confirm Password <span className={styles.required}>*</span>
              </label>
              <div className={styles.passwordContainer}>
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.password_confirmation}
                  onChange={(e) =>
                    handleInputChange("password_confirmation", e.target.value)
                  }
                  className={`${styles.inputField} pr-10 ${errors.password_confirmation
                    ? styles.inputError
                    : styles.inputDefault
                    }`}
                />
                <span
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={styles.passwordToggle}
                >
                  {showConfirmPassword ? (
                    <EyeIcon className="fill-gray-500" />
                  ) : (
                    <EyeCloseIcon className="fill-gray-500" />
                  )}
                </span>
              </div>
              {errors.password_confirmation && (
                <span className={styles.errorText}>
                  {errors.password_confirmation}
                </span>
              )}
            </div>

            <div className={styles.rolesSection}>
              <label className={styles.label}>
                Select Roles <span className={styles.required}>*</span>
              </label>
              <CustomMultiSelect
                options={roleOptions}
                value={formData.selectedRoles}
                onChange={(roles) => handleInputChange("selectedRoles", roles)}
                error={!!errors.roles}
              />
              {errors.roles && (
                <span className={styles.errorText}>{errors.roles}</span>
              )}
            </div>

            <div className={styles.rolesSection}>
              <ListboxComponent
                value={
                  jobTitles.find((job) => String(job.id) === formData.job_title) ?? null
                }
                onChange={(job) =>
                  handleInputChange("job_title", job ? String(job.id) : "")
                }
                items={jobTitles}
                label="Job Title"
                displayKey="job_title"
                error={errors.job_title}
                required
              />


            </div>

            <div>
              <label className={styles.label}>
                Job Type <span className={styles.required}>*</span>
              </label>
              <div className={styles.radioGroup}>
                {[
                  { value: "1", label: "Admin" },
                  { value: "2", label: "Regular User" },
                ].map((type) => (
                  <label key={type.value} className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="job_type"
                      value={type.value}
                      checked={formData.job_type === type.value}
                      onChange={(e) => handleInputChange("job_type", e.target.value)}
                      className={styles.radioInput}
                    />
                    {type.label}
                  </label>
                ))}
              </div>
              {errors.job_type && (
                <span className={styles.errorText}>{errors.job_type}</span>
              )}
            </div>
          </div>

          <div className={styles.buttonsContainer}>
            <button
              className={styles.submitButton}
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving..." : "Submit"}
            </button>
            <button
              className={styles.cancelButton}
              type="button"
              onClick={() => navigate("/user-list")}
            >
              Cancel
            </button>
          </div>

          <Modal
            isOpen={isSuccessModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            className={styles.modal}
          >
            <button
              onClick={() => setSuccessModalOpen(false)}
              className={styles.modalCloseButton}
            ></button>
            <div className={styles.modalIconContainer}>
              <CheckCircle className="w-6 h-6" />
            </div>
            <h4 className={styles.modalTitle}>User Added Successfully</h4>
            <p className={styles.modalMessage}>
              The user has been added to the system successfully.
            </p>
            <div className={styles.modalButtonContainer}>
              <button
                onClick={() => setSuccessModalOpen(false)}
                className={styles.modalButton}
              >
                OK
              </button>
            </div>
          </Modal>
        </form>
      </div>
    </div>
  );
};

export default UserAdd;